import React, { useState, Fragment, useCallback } from 'react';
import { Outlet, Link, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { GlobalMonitoringAlertProvider } from '../contexts/GlobalMonitoringAlertContext';
import { useUserPermissions } from '../hooks/useUserPermissions';
import { canAccessPermissionsManagement } from '../utils/permissionsAccess';
import { Menu, Transition } from '@headlessui/react';
import { toast } from 'react-hot-toast';
import ChangePasswordModal from '../components/ChangePasswordModal';
import UserPreferencesModal from '../components/UserPreferencesModal';
import GlobalAlertBar from '../components/GlobalAlertBar';
import ServiceNowNotificationBell from '../components/ServiceNowNotificationBell';
import { useTicketEvents, TicketEvent } from '../hooks/useTicketEvents';
import {
  HomeIcon,
  ExclamationCircleIcon,
  BeakerIcon,
  ArrowPathIcon,
  BriefcaseIcon,
  BellAlertIcon,
  ClipboardDocumentCheckIcon,
  BookOpenIcon,
  UserCircleIcon,
  ArrowRightOnRectangleIcon,
  Bars3Icon,
  XMarkIcon,
  ShieldCheckIcon,
  UsersIcon,
  UserGroupIcon,
  CloudIcon,
  DocumentTextIcon,
  CpuChipIcon,
  ChevronRightIcon,
  ChevronDownIcon,
  CogIcon,
  TagIcon,
  BanknotesIcon,
  DocumentDuplicateIcon,
  ServerIcon,
  CubeIcon,
  KeyIcon,
  BellIcon,
  EnvelopeIcon,
  ClipboardDocumentListIcon,
  ClockIcon,
  CommandLineIcon,
  ChartBarIcon,
  NoSymbolIcon,
  AdjustmentsHorizontalIcon,
  CalendarDaysIcon,
} from '@heroicons/react/24/outline';

const dashboardNav = { name: 'Dashboard', href: '/dashboard', icon: HomeIcon };
const monitoringNav = { name: 'Monitoring', href: '/monitoring', icon: ChartBarIcon };
const monitoringConfigurationNav = {
  name: 'Configuration',
  href: '/configuration',
  icon: AdjustmentsHorizontalIcon,
};
const kpiDashboardNav = { name: 'KPI Dashboard', href: '/kpi-dashboard', icon: HomeIcon };
const bandwidthNav = { name: 'Bandwidth', href: '/bandwidth', icon: ChartBarIcon };

const baseNavigation = [
  { name: 'Tickets', href: '/tickets', icon: DocumentTextIcon },
  { name: 'Incidents', href: '/incidents', icon: ExclamationCircleIcon },
  { name: 'Problems', href: '/problems', icon: BeakerIcon },
  { name: 'Changes', href: '/changes', icon: ArrowPathIcon },
  { name: 'Service Requests', href: '/service-requests', icon: DocumentDuplicateIcon },
  { name: 'Cases', href: '/cases', icon: BriefcaseIcon },
  { name: 'Alerts', href: '/alerts', icon: BellAlertIcon },
];

const knowledgeManagementNavigation = {
  knowledgeManagement: {
    name: 'Knowledge Management',
    icon: BookOpenIcon,
    items: [
      { name: 'Knowledge Articles', href: '/knowledge', icon: BookOpenIcon },
      { name: 'Knowledge Base', href: '/knowledge-base', icon: DocumentDuplicateIcon },
    ]
  }
};

const adminNavigation = {
  userManagement: {
    name: 'User Management',
    icon: UsersIcon,
    items: [
      { name: 'Users', href: '/users', icon: UsersIcon },
      { name: 'Contacts', href: '/contacts', icon: UsersIcon },
      { name: 'Groups', href: '/groups', icon: UserGroupIcon },
      { name: 'Permissions', href: '/admin/permissions', icon: ShieldCheckIcon },
    ]
  },
  surveyManagement: {
    name: 'Survey Management',
    icon: ClipboardDocumentCheckIcon,
    items: [
      { name: 'Surveys', href: '/surveys', icon: ClipboardDocumentCheckIcon },
      { name: 'Survey Templates', href: '/admin/survey-templates', icon: ClipboardDocumentListIcon },
      { name: 'Default Questions', href: '/admin/default-questions', icon: ClipboardDocumentListIcon },
      { name: 'Survey Email Template', href: '/admin/survey-email-templates', icon: EnvelopeIcon },
    ]
  },
  cabMeetingManagement: {
    name: 'CAB Meeting Management',
    icon: CalendarDaysIcon,
    items: [
      { name: 'All CAB Definitions', href: '/admin/cab-meeting-management/cab-definitions', icon: ClipboardDocumentListIcon },
      { name: 'CAB WorkBench', href: '/admin/cab-meeting-management/cab-workbench', icon: BriefcaseIcon },
    ]
  },
  setup: {
    name: 'Setup',
    icon: CogIcon,
    items: [
      { name: 'Customers', href: '/customers', icon: BriefcaseIcon },
      { name: 'Engagements', href: '/engagements', icon: BanknotesIcon },
      { name: 'Service Plans', href: '/service-plans', icon: DocumentDuplicateIcon },
      { name: 'Resources', href: '/resources', icon: ServerIcon },
    ]
  },
  notifications: {
    name: 'Notifications',
    icon: BellIcon,
    items: [
      { name: 'Email Templates', href: '/admin/email-templates', icon: EnvelopeIcon },
      { name: 'Notification Rules', href: '/admin/notification-rules', icon: BellIcon },
      { name: 'Notification Logs', href: '/admin/notification-logs', icon: ClipboardDocumentListIcon },
    ]
  },
  integrationLogs: {
    name: 'Integration Logs',
    icon: CommandLineIcon,
    items: [
      { name: 'LabTech Logs', href: '/admin/labtech-logs', icon: CommandLineIcon },
    ]
  },
  masters: {
    name: 'Masters',
    icon: TagIcon,
    items: [
      { name: 'Offers', href: '/offers', icon: TagIcon },
      { name: 'Variables', href: '/admin/variables', icon: CubeIcon },
      { name: 'SLA Conditions', href: '/admin/sla-conditions', icon: ShieldCheckIcon },
    ]
  },
  billing: {
    name: 'Billing',
    icon: BanknotesIcon,
    items: [
      { name: 'Billing Dashboard', href: '/billing', icon: BanknotesIcon },
      { name: 'Record Time', href: '/time-entries', icon: ClockIcon },
    ]
  }
};

/** Settings submenu items (order is applied explicitly below from permissions). */
const SETTINGS_NAV_ITEMS = {
  employeeAllocation: { name: 'Employee Allocation', href: '/admin/allocation-employees', icon: UserGroupIcon },
  caseSuppression: { name: 'Case Suppression', href: '/admin/case-suppression', icon: NoSymbolIcon },
  environments: { name: 'Environments', href: '/environments', icon: CloudIcon },
  requestedItems: { name: 'Requested Items', href: '/environment-requests', icon: DocumentTextIcon },
  workflows: { name: 'Workflows', href: '/workflows', icon: CpuChipIcon },
};

// Map ticket types to display names and routes
const TICKET_TYPE_DISPLAY: Record<string, { name: string; route: string; icon: string }> = {
  ALERT: { name: 'Alert', route: '/alerts', icon: '🔔' },
  INCIDENT: { name: 'Incident', route: '/incidents', icon: '⚠️' },
  PROBLEM: { name: 'Problem', route: '/problems', icon: '🔬' },
  CHANGE: { name: 'Change', route: '/changes', icon: '🔄' },
  CASE: { name: 'Case', route: '/cases', icon: '📋' },
  SERVICE_REQUEST: { name: 'Service Request', route: '/service-requests', icon: '📝' },
};

const MainLayout: React.FC = () => {
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [expandedSubmenus, setExpandedSubmenus] = useState<Record<string, boolean>>({
    knowledgeManagement: true, // Start expanded
    userManagement: true, // Start expanded
    surveyManagement: true, // Start expanded
    cabMeetingManagement: true, // Start expanded
    setup: true, // Start expanded
    notifications: true, // Start expanded
    integrationLogs: true, // Start expanded
    masters: true, // Start expanded
    billing: true, // Start expanded
  });
  const [isChangePasswordModalOpen, setIsChangePasswordModalOpen] = useState(false);
  const [isPreferencesModalOpen, setIsPreferencesModalOpen] = useState(false);

  const location = useLocation();
  const { user, logout, hasAnyRole } = useAuth();
  const canMonitoringConfiguration =
    Boolean(user) && hasAnyRole(['super_admin', 'admin', 'manager']);
  const { canAccessMenuItem } = useUserPermissions();

  // Handle real-time ticket events and show notifications
  const handleTicketEvent = useCallback((event: TicketEvent) => {
    // Contact users should not see any ticket creation notifications
    const isContactUser = user && ((user as any).class === 'contact' || (user as any).class === 'Contact');
    if (isContactUser) return;

    // Only show notifications for new tickets (created events)
    // Event types are like INCIDENT_CREATED, ALERT_CREATED, etc.
    if (!event.eventType.endsWith('_CREATED')) return;

    // Disable alert notifications - users don't want to see toast messages for alerts
    if (event.ticketType === 'ALERT') return;

    const ticketInfo = TICKET_TYPE_DISPLAY[event.ticketType];
    if (!ticketInfo) return;

    // Create the notification content
    const notificationContent = (
      <div className="flex items-start gap-3">
        <span className="text-2xl">{ticketInfo.icon}</span>
        <div className="flex-1 min-w-0">
          <p className="font-semibold text-gray-900">
            New {ticketInfo.name}: {event.ticketNumber}
          </p>
          {event.shortDescription && (
            <p className="text-sm text-gray-600 truncate mt-0.5">
              {event.shortDescription}
            </p>
          )}
          <Link
            to={`${ticketInfo.route}/${event.ticketId}`}
            className="text-sm text-indigo-600 hover:text-indigo-800 font-medium mt-1 inline-block"
            onClick={() => toast.dismiss()}
          >
            View Details →
          </Link>
        </div>
      </div>
    );

    // Show toast notification with custom styling
    toast.custom(
      (t) => (
        <div
          className={`${
            t.visible ? 'animate-enter' : 'animate-leave'
          } max-w-md w-full bg-white shadow-lg rounded-lg pointer-events-auto ring-1 ring-black ring-opacity-5 overflow-hidden`}
        >
          <div className="p-4">
            {notificationContent}
          </div>
          <div className="bg-gray-50 px-4 py-2 flex justify-end">
            <button
              onClick={() => toast.dismiss(t.id)}
              className="text-sm text-gray-500 hover:text-gray-700"
            >
              Dismiss
            </button>
          </div>
        </div>
      ),
      {
        duration: 8000, // Show for 8 seconds
        position: 'top-right',
      }
    );
  }, [user]);

  // Subscribe to all ticket events globally
  useTicketEvents({
    enabled: !!user, // Only connect when user is logged in
    onEvent: handleTicketEvent,
  });
  
  // Build admin navigation based on database permissions only
  const adminNav = React.useMemo(() => {
    const nav: any = {};
    if (user) {
      // User Management section - based on database permissions
      const userManagementItems = [];
      if (canAccessMenuItem('users')) {
        userManagementItems.push(adminNavigation.userManagement.items[0]); // Users
      }
      if (canAccessMenuItem('contacts')) {
        userManagementItems.push(adminNavigation.userManagement.items[1]); // Contacts
      }
      if (canAccessMenuItem('groups')) {
        userManagementItems.push(adminNavigation.userManagement.items[2]); // Groups
      }
      const canSeePermissions = canAccessPermissionsManagement(
        user as any,
        canAccessMenuItem('permissions')
      );

      if (canSeePermissions) {
        userManagementItems.push(adminNavigation.userManagement.items[3]); // Permissions
      }
      if (userManagementItems.length > 0) {
        nav.userManagement = {
          ...adminNavigation.userManagement,
          items: userManagementItems
        };
      }

      // Survey Management section - based on database permissions
      const surveyManagementItems = [];
      // GOD roles (super_admin) bypass database permission checks since they have no Permission table entries
      // For SYSTEM roles, rely on database permissions
      const canSeeSurveys = (user as any).roleType === 'GOD' ||
                            canAccessMenuItem('surveys');
      const canSeeSurveyTemplates = (user as any).roleType === 'GOD' ||
                                    canAccessMenuItem('survey_templates');

      if (canSeeSurveys) {
        surveyManagementItems.push(adminNavigation.surveyManagement.items[0]); // Surveys
      }
      if (canSeeSurveyTemplates) {
        surveyManagementItems.push(adminNavigation.surveyManagement.items[1]); // Survey Templates
        surveyManagementItems.push(adminNavigation.surveyManagement.items[2]); // Default Questions
        surveyManagementItems.push(adminNavigation.surveyManagement.items[3]); // Survey Email Template
      }
      if (surveyManagementItems.length > 0) {
        nav.surveyManagement = {
          ...adminNavigation.surveyManagement,
          items: surveyManagementItems
        };
      }

      // CAB Meeting Management:
      // Admin → Definitions + WorkBench; non-admin CAB Approver → WorkBench only
      // (CAB Approver WorkBench access is injected by /permissions/my via approval-group membership)
      const cabMeetingItems = [];
      const canSeeCabDefinitions = (user as any).roleType === 'GOD' ||
        canAccessMenuItem('cab_definitions');
      const canSeeCabWorkbench = (user as any).roleType === 'GOD' ||
        canAccessMenuItem('cab_workbench');

      if (canSeeCabDefinitions) {
        cabMeetingItems.push(adminNavigation.cabMeetingManagement.items[0]);
      }
      if (canSeeCabWorkbench) {
        cabMeetingItems.push(adminNavigation.cabMeetingManagement.items[1]);
      }
      if (cabMeetingItems.length > 0) {
        nav.cabMeetingManagement = {
          ...adminNavigation.cabMeetingManagement,
          items: cabMeetingItems,
        };
      }

      // Setup section - based on database permissions
      const setupItems = [];
      if (canAccessMenuItem('customers')) {
        setupItems.push(adminNavigation.setup.items[0]); // Customers
      }
      if (canAccessMenuItem('engagements')) {
        setupItems.push(adminNavigation.setup.items[1]); // Engagements
      }
      if (canAccessMenuItem('service_plans')) {
        setupItems.push(adminNavigation.setup.items[2]); // Service Plans
      }
      if (canAccessMenuItem('resources')) {
        setupItems.push(adminNavigation.setup.items[3]); // Resources
      }
      if (setupItems.length > 0) {
        nav.setup = {
          ...adminNavigation.setup,
          items: setupItems
        };
      }

      // Notifications section - based on database permissions
      const notificationItems = [];
      if (canAccessMenuItem('email_template')) {
        notificationItems.push(adminNavigation.notifications.items[0]); // Email Templates
      }
      if (canAccessMenuItem('notification_rule')) {
        notificationItems.push(adminNavigation.notifications.items[1]); // Notification Rules
      }
      if (canAccessMenuItem('notification_log')) {
        notificationItems.push(adminNavigation.notifications.items[2]); // Notification Logs
      }
      if (notificationItems.length > 0) {
        nav.notifications = {
          ...adminNavigation.notifications,
          items: notificationItems
        };
      }

      // Integration Logs section - based on database permissions
      const integrationLogItems = [];
      if (canAccessMenuItem('integration_logs')) {
        integrationLogItems.push(adminNavigation.integrationLogs.items[0]); // LabTech Logs
      }
      if (integrationLogItems.length > 0) {
        nav.integrationLogs = {
          ...adminNavigation.integrationLogs,
          items: integrationLogItems
        };
      }

      // Masters section - based on database permissions
      const mastersItems = [];
      if (canAccessMenuItem('offers')) {
        mastersItems.push(adminNavigation.masters.items[0]); // Offers
      }
      if (canAccessMenuItem('variable-templates')) {
        mastersItems.push(adminNavigation.masters.items[1]); // Variables
      }
      if (canAccessMenuItem('sla_conditions')) {
        mastersItems.push(adminNavigation.masters.items[2]); // SLA Conditions
      }
      if (mastersItems.length > 0) {
        nav.masters = {
          ...adminNavigation.masters,
          items: mastersItems
        };
      }

      // Billing section - based on database permissions
      const billingItems = [];
      if (canAccessMenuItem('billing')) {
        billingItems.push(adminNavigation.billing.items[0]); // Billing Dashboard
      }
      if (canAccessMenuItem('time_entries')) {
        billingItems.push(adminNavigation.billing.items[1]); // Record Time
      }
      if (billingItems.length > 0) {
        nav.billing = {
          ...adminNavigation.billing,
          items: billingItems
        };
      }
    }
    return nav;
  }, [user, canAccessMenuItem]);

  const toggleSubmenu = (submenuKey: string) => {
    setExpandedSubmenus(prev => ({
      ...prev,
      [submenuKey]: !prev[submenuKey]
    }));
  };
  
  // Check if user is a contact user
  const isContactUser = user?.class?.toLowerCase() === 'contact';

  // Filter base navigation based on database permissions and user class
  const filteredBaseNavigation = React.useMemo(() => {
    // All users see modules they have database permissions for
    return baseNavigation.filter(item => {
      const entityName = item.name.toLowerCase().replace(/\s+/g, '_');
      // Map menu names to entity names
      const entityMap: Record<string, string> = {
        'tickets': 'tickets',  // Tickets uses tickets permission
        'incidents': 'incidents',
        'problems': 'problems',
        'changes': 'changes',
        'service_requests': 'service_requests',  // Service Requests uses service_requests permission
        'cases': 'cases',
        'alerts': 'alerts',
        'surveys': 'surveys'
      };
      const entity = entityMap[entityName] || entityName;

      // Contact users should not see these menu items (they use /tickets instead)
      // Cases is allowed since contact users can access list and create new cases
      const blockedForContactUsers = ['incidents', 'problems', 'changes', 'service_requests', 'alerts', 'surveys'];
      if (isContactUser && blockedForContactUsers.includes(entity)) {
        return false;
      }

      return canAccessMenuItem(entity);
    });
  }, [canAccessMenuItem, isContactUser]);

  const showBandwidthNav = React.useMemo(() => canAccessMenuItem('bandwidth'), [canAccessMenuItem]);
  const showKpiDashboardNav = React.useMemo(() => canAccessMenuItem('kpi_dashboard'), [canAccessMenuItem]);

  // Build knowledge management navigation based on database permissions
  const knowledgeManagementNav = React.useMemo(() => {
    const nav: any = {};
    if (user) {
      const knowledgeItems = [];
      if (canAccessMenuItem('knowledge')) {
        knowledgeItems.push(knowledgeManagementNavigation.knowledgeManagement.items[0]); // Knowledge Articles
      }
      if (canAccessMenuItem('knowledge_base')) {
        knowledgeItems.push(knowledgeManagementNavigation.knowledgeManagement.items[1]); // Knowledge Base
      }
      if (knowledgeItems.length > 0) {
        nav.knowledgeManagement = {
          ...knowledgeManagementNavigation.knowledgeManagement,
          items: knowledgeItems
        };
      }
    }
    return nav;
  }, [user, canAccessMenuItem]);

  // Build settings navigation based on database permissions only
  const settingsNav = [];
  if (user) {
    if (hasAnyRole(['admin', 'super_admin'])) {
      settingsNav.push(SETTINGS_NAV_ITEMS.employeeAllocation);
    }
    if (canAccessMenuItem('case_suppression')) {
      settingsNav.push(SETTINGS_NAV_ITEMS.caseSuppression);
    }
    if (canAccessMenuItem('environments')) {
      settingsNav.push(SETTINGS_NAV_ITEMS.environments);
    }
    if (canAccessMenuItem('requests')) {
      settingsNav.push(SETTINGS_NAV_ITEMS.requestedItems);
    }
    if (canAccessMenuItem('workflows')) {
      settingsNav.push(SETTINGS_NAV_ITEMS.workflows);
    }
  }

  const isActive = React.useCallback((path: string) => {
    // Exact match or pathname starts with path followed by /
    // This prevents /knowledge from matching /knowledge-base
    return location.pathname === path || location.pathname.startsWith(path + '/');
  }, [location.pathname]);

  const isSubmenuActive = React.useCallback((items: any[]) => {
    return items.some(item => isActive(item.href));
  }, [isActive]);

  // Auto-expand submenus based on current route
  React.useEffect(() => {
    setExpandedSubmenus(prev => {
      const newState = { ...prev };
      let hasChanges = false;

      // Check knowledge management navigation
      Object.entries(knowledgeManagementNav).forEach(([key, submenu]: [string, any]) => {
        if (submenu.items && isSubmenuActive(submenu.items)) {
          if (!newState[key]) {
            newState[key] = true;
            hasChanges = true;
          }
        }
      });

      // Check admin navigation
      Object.entries(adminNav).forEach(([key, submenu]: [string, any]) => {
        if (submenu.items && isSubmenuActive(submenu.items)) {
          if (!newState[key]) {
            newState[key] = true;
            hasChanges = true;
          }
        }
      });

      return hasChanges ? newState : prev;
    });
  }, [location.pathname, knowledgeManagementNav, adminNav, isSubmenuActive]);

  return (
    <GlobalMonitoringAlertProvider>
    <div className="h-screen bg-gray-50 flex">
      {/* Mobile sidebar backdrop */}
      {sidebarOpen && (
        <div
          className="fixed inset-0 z-40 bg-gray-600 bg-opacity-75 lg:hidden"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      {/* Mobile sidebar */}
      <div
        className={`fixed inset-y-0 left-0 z-50 w-64 bg-white shadow-lg transform transition-transform lg:hidden ${
          sidebarOpen ? 'translate-x-0' : '-translate-x-full'
        }`}
      >
        <div className="flex flex-col h-full">
          <div className="flex items-center justify-between h-16 px-4 border-b flex-shrink-0">
            <div className="flex items-center space-x-2">
              <img
                src="/assets/small-tenarai.png"
                alt="Tenarai logo"
                className="h-8 w-8"
              />
              <h2 className="text-xl font-semibold text-gray-800">Pulse</h2>
            </div>
            <button
              onClick={() => setSidebarOpen(false)}
              className="p-2 rounded-md hover:bg-gray-100"
            >
              <XMarkIcon className="h-6 w-6" />
            </button>
          </div>
          <nav className="flex-1 overflow-y-auto min-h-0">
            <div className="mt-5 px-2 pb-4">
          {/* Dashboard link is reserved for contact users; everyone else lands on KPI Dashboard */}
          {isContactUser && (
            <Link
              to={dashboardNav.href}
              onClick={() => setSidebarOpen(false)}
              className={`group flex items-center px-2 py-2 text-base font-medium rounded-md transition-colors ${
                isActive(dashboardNav.href)
                  ? 'bg-primary-100 text-primary-700'
                  : 'text-gray-600 hover:bg-gray-50 hover:text-gray-900'
              }`}
            >
              <dashboardNav.icon
                className={`mr-4 h-6 w-6 ${
                  isActive(dashboardNav.href) ? 'text-primary-600' : 'text-gray-400'
                }`}
              />
              {dashboardNav.name}
            </Link>
          )}
          {(showKpiDashboardNav || showBandwidthNav) && (
            <div className="px-2 py-2 mt-4">
              <p className="text-xs font-semibold text-gray-400 uppercase tracking-wider">Dashboard</p>
            </div>
          )}
          {showKpiDashboardNav && (
            <Link
              to={kpiDashboardNav.href}
              onClick={() => setSidebarOpen(false)}
              className={`group flex items-center px-2 py-2 text-base font-medium rounded-md transition-colors ${
                isActive(kpiDashboardNav.href)
                  ? 'bg-primary-100 text-primary-700'
                  : 'text-gray-600 hover:bg-gray-50 hover:text-gray-900'
              }`}
            >
              <kpiDashboardNav.icon
                className={`mr-4 h-6 w-6 ${
                  isActive(kpiDashboardNav.href) ? 'text-primary-600' : 'text-gray-400'
                }`}
              />
              {kpiDashboardNav.name}
            </Link>
          )}
          {showBandwidthNav && (
            <Link
              to={bandwidthNav.href}
              onClick={() => setSidebarOpen(false)}
              className={`group flex items-center px-2 py-2 text-base font-medium rounded-md transition-colors ${
                isActive(bandwidthNav.href)
                  ? 'bg-primary-100 text-primary-700'
                  : 'text-gray-600 hover:bg-gray-50 hover:text-gray-900'
              }`}
            >
              <bandwidthNav.icon
                className={`mr-4 h-6 w-6 ${
                  isActive(bandwidthNav.href) ? 'text-primary-600' : 'text-gray-400'
                }`}
              />
              {bandwidthNav.name}
            </Link>
          )}

          {/* Modules section */}
          <div className="px-2 py-2 mt-4">
            <p className="text-xs font-semibold text-gray-400 uppercase tracking-wider">Modules</p>
          </div>
          {filteredBaseNavigation.map((item) => (
            <Link
              key={item.name}
              to={item.href}
              onClick={() => setSidebarOpen(false)}
              className={`group flex items-center px-2 py-2 text-base font-medium rounded-md transition-colors ${
                isActive(item.href)
                  ? 'bg-primary-100 text-primary-700'
                  : 'text-gray-600 hover:bg-gray-50 hover:text-gray-900'
              }`}
            >
              <item.icon
                className={`mr-4 h-6 w-6 ${
                  isActive(item.href) ? 'text-primary-600' : 'text-gray-400'
                }`}
              />
              {item.name}
            </Link>
          ))}
          {Object.keys(knowledgeManagementNav).length > 0 && (
            <>
              <div className="px-2 py-2 mt-4">
                <p className="text-xs font-semibold text-gray-400 uppercase tracking-wider">Knowledge Management</p>
              </div>
              {Object.entries(knowledgeManagementNav).map(([key, submenu]: [string, any]) => (
                <div key={key}>
                  <button
                    onClick={() => toggleSubmenu(key)}
                    className={`group flex items-center justify-between w-full px-2 py-2 text-base font-medium rounded-md transition-colors ${
                      isSubmenuActive(submenu.items)
                        ? 'bg-primary-100 text-primary-700'
                        : 'text-gray-600 hover:bg-gray-50 hover:text-gray-900'
                    }`}
                  >
                    <div className="flex items-center">
                      <submenu.icon
                        className={`mr-4 h-6 w-6 ${
                          isSubmenuActive(submenu.items) ? 'text-primary-600' : 'text-gray-400'
                        }`}
                      />
                      {submenu.name}
                    </div>
                    {expandedSubmenus[key] ? (
                      <ChevronDownIcon className="h-4 w-4" />
                    ) : (
                      <ChevronRightIcon className="h-4 w-4" />
                    )}
                  </button>
                  {expandedSubmenus[key] && (
                    <div className="ml-6 mt-1 space-y-1">
                      {submenu.items.map((item: any) => (
                        <Link
                          key={item.name}
                          to={item.href}
                          onClick={() => setSidebarOpen(false)}
                          className={`group flex items-center px-2 py-2 text-sm font-medium rounded-md transition-colors ${
                            isActive(item.href)
                              ? 'bg-primary-100 text-primary-700'
                              : 'text-gray-600 hover:bg-gray-50 hover:text-gray-900'
                          }`}
                        >
                          <item.icon
                            className={`mr-3 h-5 w-5 ${
                              isActive(item.href) ? 'text-primary-600' : 'text-gray-400'
                            }`}
                          />
                          {item.name}
                        </Link>
                      ))}
                    </div>
                  )}
                </div>
              ))}
            </>
          )}
          {Object.keys(adminNav).length > 0 && (
            <>
              <div className="px-2 py-2 mt-4">
                <p className="text-xs font-semibold text-gray-400 uppercase tracking-wider">Admin</p>
              </div>
              {Object.entries(adminNav).map(([key, submenu]: [string, any]) => (
                <div key={key}>
                  <button
                    onClick={() => toggleSubmenu(key)}
                    className={`group flex items-center justify-between w-full px-2 py-2 text-base font-medium rounded-md transition-colors ${
                      isSubmenuActive(submenu.items)
                        ? 'bg-primary-100 text-primary-700'
                        : 'text-gray-600 hover:bg-gray-50 hover:text-gray-900'
                    }`}
                  >
                    <div className="flex items-center">
                      <submenu.icon
                        className={`mr-4 h-6 w-6 ${
                          isSubmenuActive(submenu.items) ? 'text-primary-600' : 'text-gray-400'
                        }`}
                      />
                      {submenu.name}
                    </div>
                    {expandedSubmenus[key] ? (
                      <ChevronDownIcon className="h-4 w-4" />
                    ) : (
                      <ChevronRightIcon className="h-4 w-4" />
                    )}
                  </button>
                  {expandedSubmenus[key] && (
                    <div className="ml-6 mt-1 space-y-1">
                      {submenu.items.map((item: any) => (
                        <Link
                          key={item.name}
                          to={item.href}
                          onClick={() => setSidebarOpen(false)}
                          className={`group flex items-center px-2 py-2 text-sm font-medium rounded-md transition-colors ${
                            isActive(item.href)
                              ? 'bg-primary-100 text-primary-700'
                              : 'text-gray-600 hover:bg-gray-50 hover:text-gray-900'
                          }`}
                        >
                          <item.icon
                            className={`mr-3 h-5 w-5 ${
                              isActive(item.href) ? 'text-primary-600' : 'text-gray-400'
                            }`}
                          />
                          {item.name}
                        </Link>
                      ))}
                    </div>
                  )}
                </div>
              ))}
            </>
          )}
          {settingsNav.length > 0 && (
            <>
              <div className="px-2 py-2 mt-4">
                <p className="text-xs font-semibold text-gray-400 uppercase tracking-wider">Settings</p>
              </div>
              {settingsNav.map((item) => (
                <Link
                  key={item.name}
                  to={item.href}
                  onClick={() => setSidebarOpen(false)}
                  className={`group flex items-center px-2 py-2 text-base font-medium rounded-md transition-colors ${
                    isActive(item.href)
                      ? 'bg-primary-100 text-primary-700'
                      : 'text-gray-600 hover:bg-gray-50 hover:text-gray-900'
                  }`}
                >
                  <item.icon
                    className={`mr-4 h-6 w-6 ${
                      isActive(item.href) ? 'text-primary-600' : 'text-gray-400'
                    }`}
                  />
                  {item.name}
                </Link>
              ))}
            </>
          )}
          {!isContactUser && (
            <>
              <div className="px-2 py-2 mt-4">
                <p className="text-xs font-semibold text-gray-400 uppercase tracking-wider">Monitoring</p>
              </div>
              <Link
                to={monitoringNav.href}
                onClick={() => setSidebarOpen(false)}
                className={`group flex items-center px-2 py-2 text-base font-medium rounded-md transition-colors ${
                  isActive(monitoringNav.href)
                    ? 'bg-primary-100 text-primary-700'
                    : 'text-gray-600 hover:bg-gray-50 hover:text-gray-900'
                }`}
              >
                <monitoringNav.icon
                  className={`mr-4 h-6 w-6 ${
                    isActive(monitoringNav.href) ? 'text-primary-600' : 'text-gray-400'
                  }`}
                />
                {monitoringNav.name}
              </Link>
              {canMonitoringConfiguration && (
                <Link
                  to={monitoringConfigurationNav.href}
                  onClick={() => setSidebarOpen(false)}
                  className={`group flex items-center px-2 py-2 text-base font-medium rounded-md transition-colors ${
                    isActive(monitoringConfigurationNav.href)
                      ? 'bg-primary-100 text-primary-700'
                      : 'text-gray-600 hover:bg-gray-50 hover:text-gray-900'
                  }`}
                >
                  <monitoringConfigurationNav.icon
                    className={`mr-4 h-6 w-6 ${
                      isActive(monitoringConfigurationNav.href) ? 'text-primary-600' : 'text-gray-400'
                    }`}
                  />
                  {monitoringConfigurationNav.name}
                </Link>
              )}
            </>
          )}
          </div>
          </nav>
        </div>
      </div>

      {/* Desktop sidebar */}
      <div className="hidden lg:flex lg:w-64 lg:flex-col lg:fixed lg:inset-y-0">
        <div className="flex flex-col h-full bg-white border-r border-gray-200">
          <div className="flex items-center h-16 px-4 border-b flex-shrink-0">
            <div className="flex items-center space-x-2">
              <img
                src="/assets/small-tenarai.png"
                alt="Tenarai logo"
                className="h-8 w-8"
              />
              <h2 className="text-xl font-semibold text-gray-800">Pulse</h2>
            </div>
          </div>
          <nav className="flex-1 overflow-y-auto min-h-0">
            <div className="mt-5 px-2 pb-4 space-y-1">
            {/* Dashboard link is reserved for contact users; everyone else lands on KPI Dashboard */}
            {isContactUser && (
              <Link
                to={dashboardNav.href}
                onClick={() => setSidebarOpen(false)}
                className={`group flex items-center px-2 py-2 text-sm font-medium rounded-md transition-colors ${
                  isActive(dashboardNav.href)
                    ? 'bg-primary-100 text-primary-700'
                    : 'text-gray-600 hover:bg-gray-50 hover:text-gray-900'
                }`}
              >
                <dashboardNav.icon
                  className={`mr-3 h-5 w-5 ${
                    isActive(dashboardNav.href) ? 'text-primary-600' : 'text-gray-400'
                  }`}
                />
                {dashboardNav.name}
              </Link>
            )}
            {(showKpiDashboardNav || showBandwidthNav) && (
              <div className="px-2 py-2 mt-4">
                <p className="text-xs font-semibold text-gray-400 uppercase tracking-wider">Dashboard</p>
              </div>
            )}
            {showKpiDashboardNav && (
              <Link
                to={kpiDashboardNav.href}
                onClick={() => setSidebarOpen(false)}
                className={`group flex items-center px-2 py-2 text-sm font-medium rounded-md transition-colors ${
                  isActive(kpiDashboardNav.href)
                    ? 'bg-primary-100 text-primary-700'
                    : 'text-gray-600 hover:bg-gray-50 hover:text-gray-900'
                }`}
              >
                <kpiDashboardNav.icon
                  className={`mr-3 h-5 w-5 ${
                    isActive(kpiDashboardNav.href) ? 'text-primary-600' : 'text-gray-400'
                  }`}
                />
                {kpiDashboardNav.name}
              </Link>
            )}
            {showBandwidthNav && (
              <Link
                to={bandwidthNav.href}
                onClick={() => setSidebarOpen(false)}
                className={`group flex items-center px-2 py-2 text-sm font-medium rounded-md transition-colors ${
                  isActive(bandwidthNav.href)
                    ? 'bg-primary-100 text-primary-700'
                    : 'text-gray-600 hover:bg-gray-50 hover:text-gray-900'
                }`}
              >
                <bandwidthNav.icon
                  className={`mr-3 h-5 w-5 ${
                    isActive(bandwidthNav.href) ? 'text-primary-600' : 'text-gray-400'
                  }`}
                />
                {bandwidthNav.name}
              </Link>
            )}

            {/* Modules section */}
            <div className="px-2 py-2 mt-4">
              <p className="text-xs font-semibold text-gray-400 uppercase tracking-wider">Modules</p>
            </div>
            {filteredBaseNavigation.map((item) => (
              <Link
                key={item.name}
                to={item.href}
                onClick={() => setSidebarOpen(false)}
                className={`group flex items-center px-2 py-2 text-sm font-medium rounded-md transition-colors ${
                  isActive(item.href)
                    ? 'bg-primary-100 text-primary-700'
                    : 'text-gray-600 hover:bg-gray-50 hover:text-gray-900'
                }`}
              >
                <item.icon
                  className={`mr-3 h-5 w-5 ${
                    isActive(item.href) ? 'text-primary-600' : 'text-gray-400'
                  }`}
                />
                {item.name}
              </Link>
            ))}
            {Object.keys(knowledgeManagementNav).length > 0 && (
              <>
                <div className="px-2 py-2 mt-4">
                  <p className="text-xs font-semibold text-gray-400 uppercase tracking-wider">Knowledge Management</p>
                </div>
                {Object.entries(knowledgeManagementNav).map(([key, submenu]: [string, any]) => (
                  <div key={key}>
                    <button
                      onClick={() => toggleSubmenu(key)}
                      className={`group flex items-center justify-between w-full px-2 py-2 text-sm font-medium rounded-md transition-colors ${
                        isSubmenuActive(submenu.items)
                          ? 'bg-primary-100 text-primary-700'
                          : 'text-gray-600 hover:bg-gray-50 hover:text-gray-900'
                      }`}
                    >
                      <div className="flex items-center">
                        <submenu.icon
                          className={`mr-3 h-5 w-5 ${
                            isSubmenuActive(submenu.items) ? 'text-primary-600' : 'text-gray-400'
                          }`}
                        />
                        {submenu.name}
                      </div>
                      {expandedSubmenus[key] ? (
                        <ChevronDownIcon className="h-4 w-4" />
                      ) : (
                        <ChevronRightIcon className="h-4 w-4" />
                      )}
                    </button>
                    {expandedSubmenus[key] && (
                      <div className="ml-6 mt-1 space-y-1">
                        {submenu.items.map((item: any) => (
                          <Link
                            key={item.name}
                            to={item.href}
                            onClick={() => setSidebarOpen(false)}
                            className={`group flex items-center px-2 py-2 text-sm font-medium rounded-md transition-colors ${
                              isActive(item.href)
                                ? 'bg-primary-100 text-primary-700'
                                : 'text-gray-600 hover:bg-gray-50 hover:text-gray-900'
                            }`}
                          >
                            <item.icon
                              className={`mr-3 h-4 w-4 ${
                                isActive(item.href) ? 'text-primary-600' : 'text-gray-400'
                              }`}
                            />
                            {item.name}
                          </Link>
                        ))}
                      </div>
                    )}
                  </div>
                ))}
              </>
            )}
            {Object.keys(adminNav).length > 0 && (
              <>
                <div className="px-2 py-2 mt-4">
                  <p className="text-xs font-semibold text-gray-400 uppercase tracking-wider">Admin</p>
                </div>
                {Object.entries(adminNav).map(([key, submenu]: [string, any]) => (
                  <div key={key}>
                    <button
                      onClick={() => toggleSubmenu(key)}
                      className={`group flex items-center justify-between w-full px-2 py-2 text-sm font-medium rounded-md transition-colors ${
                        isSubmenuActive(submenu.items)
                          ? 'bg-primary-100 text-primary-700'
                          : 'text-gray-600 hover:bg-gray-50 hover:text-gray-900'
                      }`}
                    >
                      <div className="flex items-center">
                        <submenu.icon
                          className={`mr-3 h-5 w-5 ${
                            isSubmenuActive(submenu.items) ? 'text-primary-600' : 'text-gray-400'
                          }`}
                        />
                        {submenu.name}
                      </div>
                      {expandedSubmenus[key] ? (
                        <ChevronDownIcon className="h-4 w-4" />
                      ) : (
                        <ChevronRightIcon className="h-4 w-4" />
                      )}
                    </button>
                    {expandedSubmenus[key] && (
                      <div className="ml-6 mt-1 space-y-1">
                        {submenu.items.map((item: any) => (
                          <Link
                            key={item.name}
                            to={item.href}
                            onClick={() => setSidebarOpen(false)}
                            className={`group flex items-center px-2 py-2 text-sm font-medium rounded-md transition-colors ${
                              isActive(item.href)
                                ? 'bg-primary-100 text-primary-700'
                                : 'text-gray-600 hover:bg-gray-50 hover:text-gray-900'
                            }`}
                          >
                            <item.icon
                              className={`mr-3 h-4 w-4 ${
                                isActive(item.href) ? 'text-primary-600' : 'text-gray-400'
                              }`}
                            />
                            {item.name}
                          </Link>
                        ))}
                      </div>
                    )}
                  </div>
                ))}
              </>
            )}
            {settingsNav.length > 0 && (
              <>
                <div className="px-2 py-2 mt-4">
                  <p className="text-xs font-semibold text-gray-400 uppercase tracking-wider">Settings</p>
                </div>
                {settingsNav.map((item) => (
                  <Link
                    key={item.name}
                    to={item.href}
                    onClick={() => setSidebarOpen(false)}
                    className={`group flex items-center px-2 py-2 text-sm font-medium rounded-md transition-colors ${
                      isActive(item.href)
                        ? 'bg-primary-100 text-primary-700'
                        : 'text-gray-600 hover:bg-gray-50 hover:text-gray-900'
                    }`}
                  >
                    <item.icon
                      className={`mr-3 h-5 w-5 ${
                        isActive(item.href) ? 'text-primary-600' : 'text-gray-400'
                      }`}
                    />
                    {item.name}
                  </Link>
                ))}
              </>
            )}
            {!isContactUser && (
              <>
                <div className="px-2 py-2 mt-4">
                  <p className="text-xs font-semibold text-gray-400 uppercase tracking-wider">Monitoring</p>
                </div>
                <Link
                  to={monitoringNav.href}
                  onClick={() => setSidebarOpen(false)}
                  className={`group flex items-center px-2 py-2 text-sm font-medium rounded-md transition-colors ${
                    isActive(monitoringNav.href)
                      ? 'bg-primary-100 text-primary-700'
                      : 'text-gray-600 hover:bg-gray-50 hover:text-gray-900'
                  }`}
                >
                  <monitoringNav.icon
                    className={`mr-3 h-5 w-5 ${
                      isActive(monitoringNav.href) ? 'text-primary-600' : 'text-gray-400'
                    }`}
                  />
                  {monitoringNav.name}
                </Link>
                {canMonitoringConfiguration && (
                  <Link
                    to={monitoringConfigurationNav.href}
                    onClick={() => setSidebarOpen(false)}
                    className={`group flex items-center px-2 py-2 text-sm font-medium rounded-md transition-colors ${
                      isActive(monitoringConfigurationNav.href)
                        ? 'bg-primary-100 text-primary-700'
                        : 'text-gray-600 hover:bg-gray-50 hover:text-gray-900'
                    }`}
                  >
                    <monitoringConfigurationNav.icon
                      className={`mr-3 h-5 w-5 ${
                        isActive(monitoringConfigurationNav.href) ? 'text-primary-600' : 'text-gray-400'
                      }`}
                    />
                    {monitoringConfigurationNav.name}
                  </Link>
                )}
              </>
            )}
            </div>
          </nav>
        </div>
      </div>

      {/* Main content */}
      <div className="lg:pl-64 flex flex-col h-screen w-full overflow-hidden">
        <GlobalAlertBar />
        {/* Header */}
        <div className="sticky top-0 z-30 bg-gray-100 shadow flex-shrink-0">
          <div className="px-4 sm:px-6 lg:px-8">
            <div className="flex items-center justify-between h-16">
              <button
                onClick={() => setSidebarOpen(true)}
                className="p-2 rounded-md hover:bg-gray-100 lg:hidden"
              >
                <Bars3Icon className="h-6 w-6" />
              </button>

              <div className="flex-1" />

              <div className="flex items-center">
                {!isContactUser && <ServiceNowNotificationBell />}
                <Menu as="div" className="relative inline-block text-left">
                  <Menu.Button className="flex items-center space-x-2 px-3 py-2 text-sm font-medium text-gray-700 hover:text-gray-900 hover:bg-gray-100 rounded-md transition-colors">
                    <UserCircleIcon className="h-6 w-6 text-gray-400" />
                    <div className="flex flex-col">
                      <span className="text-sm font-medium text-gray-700">
                        {user?.firstName} {user?.lastName}
                      </span>
                    </div>
                    <ChevronDownIcon className="h-4 w-4 text-gray-500" />
                  </Menu.Button>

                  <Transition
                    as={Fragment}
                    enter="transition ease-out duration-100"
                    enterFrom="transform opacity-0 scale-95"
                    enterTo="transform opacity-100 scale-100"
                    leave="transition ease-in duration-75"
                    leaveFrom="transform opacity-100 scale-100"
                    leaveTo="transform opacity-0 scale-95"
                  >
                    <Menu.Items className="absolute right-0 mt-2 w-56 origin-top-right divide-y divide-gray-100 rounded-md bg-white shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none">
                      <div className="px-1 py-1">
                        <Menu.Item>
                          {({ active }) => (
                            <button
                              onClick={() => setIsPreferencesModalOpen(true)}
                              className={`${
                                active ? 'bg-primary-50 text-primary-700' : 'text-gray-900'
                              } group flex w-full items-center rounded-md px-2 py-2 text-sm`}
                            >
                              <CogIcon className="mr-2 h-5 w-5" aria-hidden="true" />
                              Preferences
                            </button>
                          )}
                        </Menu.Item>
                        <Menu.Item>
                          {({ active }) => (
                            <button
                              onClick={() => setIsChangePasswordModalOpen(true)}
                              className={`${
                                active ? 'bg-primary-50 text-primary-700' : 'text-gray-900'
                              } group flex w-full items-center rounded-md px-2 py-2 text-sm`}
                            >
                              <KeyIcon className="mr-2 h-5 w-5" aria-hidden="true" />
                              Change Password
                            </button>
                          )}
                        </Menu.Item>
                        <Menu.Item>
                          {({ active }) => (
                            <button
                              onClick={() => logout('USER_LOGOUT')}
                              className={`${
                                active ? 'bg-primary-50 text-primary-700' : 'text-gray-900'
                              } group flex w-full items-center rounded-md px-2 py-2 text-sm`}
                            >
                              <ArrowRightOnRectangleIcon className="mr-2 h-5 w-5" aria-hidden="true" />
                              Logout
                            </button>
                          )}
                        </Menu.Item>
                      </div>
                    </Menu.Items>
                  </Transition>
                </Menu>
              </div>
            </div>
          </div>
        </div>

        {/* Change Password Modal */}
        <ChangePasswordModal
          isOpen={isChangePasswordModalOpen}
          onClose={() => setIsChangePasswordModalOpen(false)}
        />

        {/* User Preferences Modal */}
        <UserPreferencesModal
          isOpen={isPreferencesModalOpen}
          onClose={() => setIsPreferencesModalOpen(false)}
        />

        {/* Page content */}
        <main className="flex-1 min-h-0 overflow-y-auto bg-gray-50 relative z-10">
          <Outlet />
        </main>
        
        {/* Footer */}
        <footer className="bg-white border-t border-gray-200 py-4 flex-shrink-0">
          <div className="px-8 flex justify-between items-center">
            <p className="text-left text-sm text-gray-500 pl-4">
              © Copyright Tenarai
            </p>
            {user?.email && (
              <p className="text-right text-sm text-gray-500 pr-4">
                Logged in as: {user.email}
              </p>
            )}
          </div>
        </footer>
      </div>
    </div>
    </GlobalMonitoringAlertProvider>
  );
};

export default MainLayout; 